import { errorWords } from "@alm/domain";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { orpc } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";
import { TemplateCreateForm, templateCreateToInput } from "./-components/template-create-form";

export const Route = createFileRoute(
  "/_authenticated/_administration/settings/document-templates/new",
)({
  beforeLoad: ({ context }) => guardRoute(context.me, "document-templates"),
  component: NewDocumentTemplatePage,
});

/** A template starts as a draft with nothing on it; the PDF and the boxes come next. */
function NewDocumentTemplatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const categories = useQuery(
    orpc.administration.documentTemplates.categories.list.queryOptions({
      input: { includeInactive: false },
    }),
  );
  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );

  const create = useMutation(
    orpc.administration.documentTemplates.create.mutationOptions({
      onSuccess: async (template) => {
        toast.success("Template created. Attach a PDF and place the fields next.");
        await queryClient.invalidateQueries({
          queryKey: orpc.administration.documentTemplates.key(),
        });
        await navigate({
          to: "/settings/document-templates/$templateId",
          params: { templateId: template.id },
        });
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="New document template"
        meta={
          <Link to="/settings/document-templates" className="hover:underline">
            Document templates
          </Link>
        }
      />
      <Card className="max-w-2xl">
        <CardContent className="pt-6">
          <TemplateCreateForm
            categories={categories.data ?? []}
            facilities={facilities.data ?? []}
            pending={create.isPending}
            onCancel={() => {
              void navigate({ to: "/settings/document-templates" });
            }}
            onSubmit={(values) => create.mutate(templateCreateToInput(values))}
          />
        </CardContent>
      </Card>
    </div>
  );
}
